import React, { ForwardedRef, forwardRef, useEffect, useMemo, useState } from "react";
import useIsMobile from "../../../../hooks/use-is-mobile";
import useClx from "../../../../hooks/use-clx";
import Collapse from "../../../Collapse";
import clxs from "./amenities.module.css";
import CHECK_ICON from "../../../../assets/icons/amenities-default-icon.svg";

interface AmenitiesProps {
  amenities: Amenity[]
  className?: string;
  isCluster?:boolean;
}

const Amenities = forwardRef(
    function AmenitiesCore(
      props: AmenitiesProps,
      ref: ForwardedRef<HTMLDivElement>,
    ) {
  const { isMobile } = useIsMobile(),
    { amenities, isCluster } = props,
    primary = useMemo(
      () => amenities.slice(0, 8),
      [amenities, isMobile],
    ),
    secondary = useMemo(
      () => amenities.slice(8),
      [amenities, isMobile],
    ),
    { className } = props,
    [collapsed, setCollapsed] = useState<boolean>(false),
    // State to track if any amenity has common amenities with propertyNames
    [hasClusterAmenities, setHasClusterAmenities] = useState(false),
    ccx = useClx(clxs.container, className);

    // On amenities change, check if at least one item has propertyNames with length
    useEffect(() => {
      const hasAmenities = amenities?.some(each => each.propertyNames?.length);
      setHasClusterAmenities(hasAmenities);
    }, [amenities]);

  return (
    <div 
      className={ccx}
      ref={ref}
    >
      <div className={clxs.title}>
        <div>Amenities</div>
        {isCluster && <div className={clxs.commonAmenities}>Common Amenities</div>}
      </div>
      {isCluster ? 
      <div className={clxs.responsiveTitle}>
        <div>Amenities</div>
        <div className={clxs.commonAmenities}>Common Amenities</div>
      </div>
      :
      <div className={clxs.responsiveTitle}>
        Every stay includes
      </div> }
      {primary.map(
        (each, i) => {
          const { icon, name, is_not_available } = each,
            cx = icon ? clxs.icon : clxs.generic,
            borderClass = i % 2 === 0 ? clxs.centerBorder : ""; // Apply only on even index

          return (
            <div
              title={name}
              className={ `${clxs.amenity} ${borderClass} ${ is_not_available ?clxs.strikeAmenity : ""}`}
              key={i}
            >
              <img
                src={icon || CHECK_ICON}
                alt={name}
                className={cx}
                style={is_not_available ? {opacity:0.6}:{opacity:1}}
              />
              {name}
            </div>
          );
        },
      )}
      <Collapse
        expanded={collapsed}
        className={`${clxs.collapseInner} ${hasClusterAmenities ? clxs.hasClusterAmenities : ""}`}
        outerClassName={clxs.collapse}
      >
        {secondary.map(
          (each, i) => {
            const { icon, name,is_not_available } = each,
              cx = icon ? clxs.icon : clxs.generic,
              borderClass = i % 2 === 0 ? clxs.centerBorder : ""; // Apply only on even index

            return (
              <div
                className={ `${clxs.amenity} ${borderClass} ${ is_not_available ?clxs.strikeAmenity : ""}`}
                key={i}
              >
                <img
                  src={icon || CHECK_ICON}
                  alt={name}
                  className={cx}
                  style={is_not_available ? {opacity:0.6}:{opacity:1}}
                />
                {name}
              </div>
            );
          },
        )}

    {isCluster && hasClusterAmenities &&(
      <div className={clxs.clusterAmenities}>
        {secondary
          .filter(each => each.propertyNames?.length) // ✅ Only items with propertyNames
          .map((each, i) => {
            const { icon, name, propertyNames } = each,
              cx = icon ? clxs.icon : clxs.generic,
              borderClass = i % 2 === 0 ? clxs.centerBorder : ""; // Apply only on even index
    
            return (
              <div
                className={`${clxs.amenity} ${borderClass}`}
                key={i}
              >
                <img
                  src={icon || CHECK_ICON}
                  alt={name}
                  className={cx}
                />
                {name}
                {propertyNames && propertyNames.length > 0 && (
                  <span className={clxs.clusterAmenityText}>
                    {isMobile ? propertyNames : ` - ${propertyNames}`}
                  </span>
                )}
              </div>
            );
          })}
      </div>
    )}
      </Collapse>
      <div
        data-collapsed={collapsed}
        className={clxs.viewAll}
        onClick={setCollapsed.bind(null, !collapsed)}
      >
        {collapsed ? "Hide Amenities" : "View All Amenities"}
      </div>
    </div>
  );
})

export default Amenities;
